import { createSlice } from '@reduxjs/toolkit';
import { AppDispatch } from 'redux/store';
import {
  getHrmAssessmentQuestions,
  getMindgageMetrics,
  HrmAnswerSubmission,
  initiateMindgageAssessment,
  submitHrmAssessmentAnswers
} from 'requests/assessment';
import { HrmQuestion } from 'types/HrmAssessment';

interface AssessmentState {
  loading: boolean;
  submitting: boolean;
  initiatingMindgage: boolean;
  questions: HrmQuestion[];
  error: string | null;
  submitSuccess: boolean;
  mindgageMetrics: any;
  mindgageTestUrl: string | null;
}

const initialState: AssessmentState = {
  loading: false,
  submitting: false,
  initiatingMindgage: false,
  questions: [],
  error: null,
  submitSuccess: false,
  mindgageMetrics: [],
  mindgageTestUrl: null
};

const slice = createSlice({
  name: 'assessment',
  initialState,
  reducers: {
    startLoading(state) {
      state.loading = true;
    },
    getQuestionsSuccess(state, action) {
      state.loading = false;
      state.questions = action.payload;
      state.error = null;
    },
    hasError(state, action) {
      state.loading = false;
      state.error = action.payload;
    },
    startSubmitting(state) {
      state.submitting = true;
      state.submitSuccess = false;
    },
    submitSuccess(state) {
      state.submitting = false;
      state.submitSuccess = true;
      state.error = null;
    },
    submitError(state, action) {
      state.submitting = false;
      state.submitSuccess = false;
      state.error = action.payload;
    },
    resetSubmitStatus(state) {
      state.submitSuccess = false;
    },
    getMindgageMetricsSuccess(state, action) {
      state.loading = false;
      state.mindgageMetrics = action.payload;
      state.error = null;
    },
    startInitiatingMindgage(state) {
      state.initiatingMindgage = true;
      state.error = null;
      state.mindgageTestUrl = null;
    },
    initiateMindgageSuccess(state, action) {
      state.initiatingMindgage = false;
      state.mindgageTestUrl = action.payload.test_logon_url;
      state.error = null;
    },
    initiateMindgageError(state, action) {
      state.initiatingMindgage = false;
      state.error = action.payload;
      state.mindgageTestUrl = null;
    }
  }
});

// Reducer
export default slice.reducer;

// Actions
export const {
  startLoading,
  getQuestionsSuccess,
  hasError,
  startSubmitting,
  submitSuccess,
  submitError,
  resetSubmitStatus,
  getMindgageMetricsSuccess,
  startInitiatingMindgage,
  initiateMindgageSuccess,
  initiateMindgageError
} = slice.actions;

// Thunks
export const fetchHrmQuestions = () => async (dispatch: AppDispatch) => {
  dispatch(startLoading());
  try {
    const response = await getHrmAssessmentQuestions();
    dispatch(getQuestionsSuccess(response));
  } catch (error) {
    dispatch(
      hasError(
        error instanceof Error ? error.message : 'Failed to fetch questions'
      )
    );
  }
};

export const submitHrmAnswers =
  (
    assessmentId: string | number,
    answers: HrmAnswerSubmission[],
    testStartTime: string,
    testEndTime: string
  ) =>
  async (dispatch: AppDispatch) => {
    dispatch(startSubmitting());
    try {
      await submitHrmAssessmentAnswers(
        assessmentId,
        answers,
        testStartTime,
        testEndTime
      );
      dispatch(submitSuccess());

      // Redirect to candidate profile page after successful submission
      window.location.href = '/candidates/me/';

      return true;
    } catch (error) {
      dispatch(
        submitError(
          error instanceof Error ? error.message : 'Failed to submit answers'
        )
      );
      return false;
    }
  };

export const fetchMindgageMetrics =
  (language: string) => async (dispatch: AppDispatch) => {
    dispatch(startLoading());
    try {
      const response = await getMindgageMetrics(language);
      dispatch(getMindgageMetricsSuccess(response));
    } catch (error) {
      dispatch(
        hasError(
          error instanceof Error
            ? error.message
            : 'Failed to fetch mindgage metrics'
        )
      );
    }
  };

export const startMindgageAssessment = () => async (dispatch: AppDispatch) => {
  dispatch(startInitiatingMindgage());
  try {
    const response = await initiateMindgageAssessment();
    dispatch(initiateMindgageSuccess(response));
    // Redirect to test logon URL
    if (response.test_logon_url) {
      return response;
    }
    return null;
  } catch (error) {
    dispatch(
      initiateMindgageError(
        error instanceof Error
          ? error.message
          : 'Failed to initiate Mindgage assessment'
      )
    );
    return null;
  }
};
